"use client";

import { forwardRef, useState, useRef, useEffect } from "react";
import { cn } from "~/lib/utils";
import { ChevronDownIcon, XIcon } from "~/components/icons";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  size?: "sm" | "md" | "lg";
}

const sizeStyles = {
  sm: {
    trigger: "h-8 px-3 text-sm",
    icon: "h-4 w-4",
    badge: "px-1.5 py-0.5 text-[10px]",
    badgeIcon: "h-2.5 w-2.5",
    badgeGap: "gap-1",
    dropdownItem: "px-2 py-1.5 text-xs",
    checkbox: "h-3.5 w-3.5",
    checkboxIcon: "h-2.5 w-2.5",
  },
  md: {
    trigger: "h-10 px-4 text-sm",
    icon: "h-4 w-4",
    badge: "px-2 py-1 text-xs",
    badgeIcon: "h-3 w-3",
    badgeGap: "gap-1.5",
    dropdownItem: "px-3 py-2 text-sm",
    checkbox: "h-4 w-4",
    checkboxIcon: "h-3 w-3",
  },
  lg: {
    trigger: "h-[60px] px-4 text-base",
    icon: "h-5 w-5",
    badge: "px-2 py-1 text-xs",
    badgeIcon: "h-3 w-3",
    badgeGap: "gap-1.5",
    dropdownItem: "px-3 py-2 text-sm",
    checkbox: "h-4 w-4",
    checkboxIcon: "h-3 w-3",
  },
};

const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      className,
      options,
      value,
      onChange,
      placeholder = "Select...",
      disabled = false,
      label,
      size = "md",
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const styles = sizeStyles[size];

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isOpen]);

    const handleToggle = (optionValue: string) => {
      if (value.includes(optionValue)) {
        onChange(value.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    };

    const handleRemove = (optionValue: string, e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(value.filter((v) => v !== optionValue));
    };

    const selectedOptions = options.filter((opt) => value.includes(opt.value));

    return (
      <div ref={ref} className={cn("flex flex-col gap-2", className)} {...props}>
        {label && (
          <label className="text-sm font-medium leading-5 text-[#1c2024]">
            {label}
          </label>
        )}
        <div className="relative" ref={containerRef}>
          {/* Trigger */}
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={cn(
              "flex w-full items-center justify-between rounded-md border",
              "border-[#d9d9e0] bg-white py-0",
              "transition-colors hover:border-[#8b8d98] focus:border-[#1c3144]",
              "focus:outline-none focus:ring-2 focus:ring-[rgba(0,85,199,0.1)]",
              disabled && "cursor-not-allowed opacity-50",
              "relative",
              styles.trigger
            )}
          >
            <span className={cn("flex-1 text-left leading-6 text-[#1c2024]", size === "sm" && "leading-5")}>
              {value.length === 0 ? (
                <span className="text-[#60646c]">{placeholder}</span>
              ) : (
                <span>&nbsp;</span>
              )}
            </span>
            <ChevronDownIcon
              className={cn(
                "text-[#60646c] transition-transform",
                isOpen && "rotate-180",
                styles.icon
              )}
            />
          </button>

          {/* Selected badges - positioned inside the input */}
          {selectedOptions.length > 0 && (
            <div className={cn(
              "pointer-events-none absolute top-1/2 flex -translate-y-1/2 flex-wrap gap-2",
              size === "sm" ? "left-3" : "left-4"
            )}>
              {selectedOptions.map((option) => (
                <div
                  key={option.value}
                  className={cn(
                    "pointer-events-auto flex items-center rounded bg-[rgba(0,116,221,0.09)]",
                    styles.badge,
                    styles.badgeGap
                  )}
                >
                  <button
                    type="button"
                    onClick={(e) => handleRemove(option.value, e)}
                    className="flex items-center text-[rgba(0,50,97,0.7)] transition-colors hover:text-[rgba(0,50,97,1)]"
                  >
                    <XIcon className={styles.badgeIcon} />
                  </button>
                  <span className="font-semibold leading-4 text-[rgba(0,50,97,0.7)]">
                    {option.label}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Dropdown */}
          {isOpen && (
            <div
              className={cn(
                "absolute left-0 top-[calc(100%+4px)] z-50 w-full",
                "rounded border border-[rgba(0,0,51,0.06)] bg-white shadow-lg",
                "max-h-[300px] overflow-y-auto"
              )}
            >
              <div className="p-2">
                {options.map((option) => {
                  const isChecked = value.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleToggle(option.value)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded",
                        "text-[#1c2024] transition-colors",
                        "hover:bg-[#f9f9fb]",
                        styles.dropdownItem
                      )}
                    >
                      <div
                        className={cn(
                          "flex items-center justify-center rounded",
                          "border transition-colors",
                          isChecked
                            ? "border-[#1c3144] bg-[#1c3144]"
                            : "border-[rgba(0,6,46,0.2)] bg-white",
                          styles.checkbox
                        )}
                      >
                        {isChecked && (
                          <svg
                            className={cn("text-white", styles.checkboxIcon)}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="flex-1 text-left">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

MultiSelect.displayName = "MultiSelect";

export { MultiSelect };
